/**
 * Catálogos de eventos: acciones y entidades de auditoría, tipos de
 * notificación y movimientos del libro del préstamo.
 */
import { keyedEnum } from './enum.js';
export { keyedEnum } from './enum.js';
/**
 * Catálogo de acciones de auditoría (`entidad.verbo`). Centralizarlas evita
 * cadenas sueltas y permite a la UI traducir cada acción a español.
 */
export const AUDIT_ACTIONS = {
    authLogin: 'auth.login',
    authLogout: 'auth.logout',
    authPasswordChanged: 'auth.password_changed',
    authSessionRevoked: 'auth.session_revoked',
    authLoginFailed: 'auth.login_failed',
    authTwoFactorEnabled: 'auth.two_factor_enabled',
    authTwoFactorDisabled: 'auth.two_factor_disabled',
    authTwoFactorFailed: 'auth.two_factor_failed',
    authRecoveryCodeUsed: 'auth.recovery_code_used',
    authRecoveryCodesRegenerated: 'auth.recovery_codes_regenerated',
    employeeTwoFactorReset: 'employee.two_factor_reset',
    employeeSessionsRevoked: 'employee.sessions_revoked',
    employeeCreated: 'employee.created',
    employeeUpdated: 'employee.updated',
    employeeProfileUpdated: 'employee.profile_updated',
    employeePasswordReset: 'employee.password_reset',
    employeeDeleted: 'employee.deleted',
    settingsUpdated: 'settings.updated',
    branchCreated: 'branch.created',
    branchUpdated: 'branch.updated',
    productCreated: 'product.created',
    productUpdated: 'product.updated',
    clientCreated: 'client.created',
    clientUpdated: 'client.updated',
    clientDeleted: 'client.deleted',
    clientBusinessAdded: 'client.business_added',
    clientReferenceAdded: 'client.reference_added',
    clientReferenceUpdated: 'client.reference_updated',
    clientReferenceRemoved: 'client.reference_removed',
    loanApplicationCreated: 'loan.application_created',
    loanApplicationUpdated: 'loan.application_updated',
    loanStatusChanged: 'loan.status_changed',
    loanApproved: 'loan.approved',
    loanRejected: 'loan.rejected',
    loanDisbursed: 'loan.disbursed',
    loanWrittenOff: 'loan.written_off',
    loanReassigned: 'loan.reassigned',
    collateralRegistered: 'collateral.registered',
    guarantorRegistered: 'guarantor.registered',
    assessmentRegistered: 'assessment.registered',
    fieldVerificationRegistered: 'field_verification.registered',
    paymentPosted: 'payment.posted',
    paymentReversed: 'payment.reversed',
    paymentsExported: 'payment.exported',
    invoiceCertified: 'invoice.certified',
    invoiceCertificationFailed: 'invoice.certification_failed',
    invoiceAnnulled: 'invoice.annulled',
    invoiceAnnulmentFailed: 'invoice.annulment_failed',
    invoiceRetryRequested: 'invoice.retry_requested',
    invoiceXmlDownloaded: 'invoice.xml_downloaded',
    cashOpened: 'cash.opened',
    cashMovement: 'cash.movement',
    cashClosed: 'cash.closed',
    cashClosedWithDifference: 'cash.closed_with_difference',
    collectionActionRecorded: 'collection.action_recorded',
    collectorAssigned: 'collection.collector_assigned',
    complianceAlertResolved: 'compliance.alert_resolved',
    documentUploaded: 'document.uploaded',
    documentDeleted: 'document.deleted',
    accountingPeriodClosed: 'accounting.period_closed',
    accountingPeriodReopened: 'accounting.period_reopened',
    accountingAdjustmentsRun: 'accounting.adjustments_run',
    accountingOpeningBalancePosted: 'accounting.opening_balance_posted',
    accountingJournalExported: 'accounting.journal_exported',
};
/** Etiquetas en español de cada acción de auditoría. */
export const AUDIT_ACTION_LABELS = {
    'auth.login': 'Inicio de sesión',
    'auth.logout': 'Cierre de sesión',
    'auth.password_changed': 'Cambio de contraseña',
    'auth.session_revoked': 'Sesión revocada',
    'auth.login_failed': 'Inicio de sesión fallido',
    'auth.two_factor_enabled': 'Verificación en dos pasos activada',
    'auth.two_factor_disabled': 'Verificación en dos pasos desactivada',
    'auth.two_factor_failed': 'Código de verificación incorrecto',
    'auth.recovery_code_used': 'Código de recuperación usado',
    'auth.recovery_codes_regenerated': 'Códigos de recuperación regenerados',
    'employee.two_factor_reset': 'Verificación en dos pasos restablecida',
    'employee.sessions_revoked': 'Sesiones del usuario cerradas',
    'employee.created': 'Usuario creado',
    'employee.updated': 'Usuario modificado',
    'employee.profile_updated': 'Perfil actualizado',
    'employee.password_reset': 'Contraseña restablecida',
    'employee.deleted': 'Usuario eliminado',
    'settings.updated': 'Configuración modificada',
    'branch.created': 'Agencia creada',
    'branch.updated': 'Agencia modificada',
    'product.created': 'Producto creado',
    'product.updated': 'Producto modificado',
    'client.created': 'Cliente registrado',
    'client.updated': 'Cliente modificado',
    'client.deleted': 'Cliente dado de baja',
    'client.business_added': 'Negocio agregado',
    'client.reference_added': 'Referencia agregada',
    'client.reference_updated': 'Referencia modificada',
    'client.reference_removed': 'Referencia dada de baja',
    'loan.application_created': 'Solicitud ingresada',
    'loan.application_updated': 'Solicitud modificada',
    'loan.status_changed': 'Cambio de etapa',
    'loan.approved': 'Aprobado en comité',
    'loan.rejected': 'Rechazado',
    'loan.disbursed': 'Desembolsado',
    'loan.written_off': 'Castigado',
    'loan.reassigned': 'Reasignado',
    'collateral.registered': 'Garantía registrada',
    'guarantor.registered': 'Fiador registrado',
    'assessment.registered': 'Estudio financiero registrado',
    'field_verification.registered': 'Verificación de campo registrada',
    'payment.posted': 'Pago registrado',
    'payment.reversed': 'Pago reversado',
    'payment.exported': 'Pagos exportados a CSV',
    'invoice.certified': 'Factura electrónica certificada',
    'invoice.certification_failed': 'Factura electrónica rechazada o sin respuesta',
    'invoice.annulled': 'Factura electrónica anulada',
    'invoice.annulment_failed': 'Anulación de factura electrónica fallida',
    'invoice.retry_requested': 'Reintento de factura electrónica',
    'invoice.xml_downloaded': 'XML de factura electrónica descargado',
    'cash.opened': 'Caja abierta',
    'cash.movement': 'Movimiento de caja',
    'cash.closed': 'Caja cerrada',
    'cash.closed_with_difference': 'Caja cerrada con diferencia',
    'collection.action_recorded': 'Gestión de cobro',
    'collection.collector_assigned': 'Gestor asignado',
    'compliance.alert_resolved': 'Alerta de cumplimiento resuelta',
    'document.uploaded': 'Documento cargado',
    'document.deleted': 'Documento eliminado',
    'accounting.period_closed': 'Período contable cerrado',
    'accounting.period_reopened': 'Período contable reabierto',
    'accounting.adjustments_run': 'Ajustes contables de cierre',
    'accounting.opening_balance_posted': 'Saldos contables de apertura',
    'accounting.journal_exported': 'Libro diario exportado a CSV',
};
/**
 * Acciones que forman la sección «Seguridad» de la bitácora: accesos,
 * segundo factor, sesiones y contraseñas. La bitácora las filtra juntas con
 * `?category=security`.
 */
export const SECURITY_AUDIT_ACTIONS = [
    AUDIT_ACTIONS.authLogin,
    AUDIT_ACTIONS.authLoginFailed,
    AUDIT_ACTIONS.authLogout,
    AUDIT_ACTIONS.authPasswordChanged,
    AUDIT_ACTIONS.authSessionRevoked,
    AUDIT_ACTIONS.authTwoFactorEnabled,
    AUDIT_ACTIONS.authTwoFactorDisabled,
    AUDIT_ACTIONS.authTwoFactorFailed,
    AUDIT_ACTIONS.authRecoveryCodeUsed,
    AUDIT_ACTIONS.authRecoveryCodesRegenerated,
    AUDIT_ACTIONS.employeeTwoFactorReset,
    AUDIT_ACTIONS.employeeSessionsRevoked,
    AUDIT_ACTIONS.employeePasswordReset,
];
/** Categorías de la bitácora que agrupan varias acciones (filtro `category`). */
export const AUDIT_CATEGORIES = ['security'];
/** Catálogo con clave de categorías: `AUDIT_CATEGORY.security`. */
export const AUDIT_CATEGORY = keyedEnum(AUDIT_CATEGORIES);
/** Acciones que incluye cada categoría. */
export const AUDIT_CATEGORY_ACTIONS = {
    security: SECURITY_AUDIT_ACTIONS,
};
/** Nombre en español de cada categoría. */
export const AUDIT_CATEGORY_LABELS = {
    security: 'Seguridad',
};
/** Tipos de entidad auditada. */
export const AUDIT_ENTITIES = {
    session: 'session',
    employee: 'employee',
    settings: 'settings',
    branch: 'branch',
    product: 'product',
    client: 'client',
    loan: 'loan',
    payment: 'payment',
    invoice: 'invoice',
    cashSession: 'cash_session',
    complianceAlert: 'compliance_alert',
    document: 'document',
    accountingPeriod: 'accounting_period',
    journalEntry: 'journal_entry',
};
/** Tipos de notificación interna (campana y Web Push). */
export const NOTIFICATION_TYPES = {
    loanStageChanged: 'loan.stage_changed',
    loanPaidOff: 'loan.paid_off',
    loanDecided: 'loan.decided',
    promiseDue: 'collection.promise_due',
    promiseBroken: 'collection.promise_broken',
    /** Promesa capturada sin conexión y cubierta con pagos anteriores a su llegada: verificar. */
    promiseNeedsVerification: 'collection.promise_needs_verification',
    /** Gestión "Pagó" sin pago registrado después del plazo de gracia: posible efectivo fuera de caja. */
    paidClaimUnverified: 'collection.paid_claim_unverified',
    complianceAlert: 'compliance.alert',
    cashDifference: 'cash.difference',
};
/** Tipos de movimiento en el libro del préstamo (guardián de concurrencia). */
export const LEDGER_EVENT_KINDS = {
    disbursement: 'disbursement',
    payment: 'payment',
    reversal: 'reversal',
};
//# sourceMappingURL=events.js.map